"use client";

import { cn } from "@/lib/cn";
import { Link, usePathname } from "@/i18n/navigation";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { BottomNav, type BottomNavItem } from "@/components/ui/BottomNav";
import { IconLeaf } from "@/components/ui/icons";

/**
 * Chrome de navegación para las PWA de cliente y terapeuta.
 * - Desktop (>= md): barra superior con marca, links horizontales y locale switcher.
 * - Mobile (< md): BottomNav fija abajo (los items se pasan desde el nav de cada
 *   superficie, que es también client component porque los íconos son componentes).
 *
 * Se usa DENTRO de cada layout de grupo. Los layouts (server) definen su nav en un
 * client component hermano y lo pasan como `items` + `brand`.
 */
export function SurfaceChrome({
  items,
  brand,
  children,
  notificationBell,
}: {
  items: BottomNavItem[];
  brand: string;
  children: React.ReactNode;
  /** Campana de notificaciones (fase 5) — ver `@/components/NotificationBell`. */
  notificationBell?: React.ReactNode;
}) {
  const pathname = usePathname();

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Top bar — desktop */}
      <header className="sticky top-0 z-30 hidden border-b border-sand-300 bg-bone-50/90 backdrop-blur md:block">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-6">
          <Link href="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest text-bone-50">
              <IconLeaf size={18} />
            </span>
            <span className="font-display text-lg text-ink">{brand}</span>
          </Link>
          <nav className="flex items-center gap-1">
            {items.map((it) => {
              const Icon = it.icon;
              const active = it.exact
                ? pathname === it.href
                : pathname === it.href || pathname.startsWith(it.href + "/");
              return (
                <Link
                  key={it.href}
                  href={it.href}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                    active
                      ? "bg-forest-100 text-forest-strong"
                      : "text-ink-muted hover:bg-sand-100 hover:text-ink-soft",
                  )}
                >
                  <Icon size={18} />
                  {it.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            {notificationBell}
            <LocaleSwitcher />
          </div>
        </div>
      </header>

      {/* Top bar compacta — mobile (marca + locale) */}
      <div className="flex items-center justify-between border-b border-sand-300 bg-bone-50/90 px-4 py-3 backdrop-blur md:hidden">
        <Link href="/" className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-forest text-bone-50">
            <IconLeaf size={16} />
          </span>
          <span className="font-display text-base text-ink">{brand}</span>
        </Link>
        <div className="flex items-center gap-2">
          {notificationBell}
          <LocaleSwitcher />
        </div>
      </div>

      {/* Contenido — padding inferior para no quedar bajo la BottomNav en mobile */}
      <main className="flex-1 pb-24 md:pb-10">{children}</main>

      <BottomNav items={items} />
    </div>
  );
}
